import type { LocationQuery, LocationQueryValue } from 'vue-router';
import { QUANTITY_OPTIONS } from '@/shared/config';
import type { MemberSearchField, SearchFilter } from '../types/search.types';
import { toSortOrder, type SortOrder } from '../types/sort.types';

/**
 * Query keys owned by the search state itself — everything else in the URL is treated as a filter.
 */
export const RESERVED_QUERY_KEYS = ['term', 'sort', 'order', 'searchType', 'page', 'searchField'];

export interface ParsedSearchQuery {
  term?: string;
  sort?: string;
  order?: SortOrder;
  searchType?: 'name' | 'prefix';
  searchField?: MemberSearchField;
  page?: number;
  /**
   * Only screens that opt into `persistQuantityInUrl` read this. The key stays out of
   * `RESERVED_QUERY_KEYS` on purpose: reserving it is global and would change the screens that
   * already read `?amount=` on their own.
   */
  amount?: number;
  filter: SearchFilter;
}

function isMemberSearchField(value: unknown): value is MemberSearchField {
  return value === 'name' || value === 'registration';
}

/** The value shape `SearchFilter` accepts, named so the union is declared in one place. */
type SearchFilterValue = string | number | boolean | Date | null | undefined;

/** What a single key of `LocationQuery` can hold, named because three parsers take it. */
type RawQueryValue = LocationQueryValue | LocationQueryValue[] | undefined;

/**
 * Converts a URL query value to a SearchFilter-compatible value.
 * URL query values are always strings (or null), so we preserve them as strings.
 */
function convertQueryValueToFilterValue(
  value: RawQueryValue
): SearchFilterValue {
  if (value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    // Take the first value from the array
    return value[0] ?? null;
  }

  // LocationQueryValue is string | null, which is compatible with SearchFilter's string type
  return value;
}

/**
 * Collects every non-reserved query key into a SearchFilter.
 */
export function extractFilterFromQuery(query: LocationQuery): SearchFilter {
  return Object.keys(query).reduce<SearchFilter>((filter, key) => {
    if (RESERVED_QUERY_KEYS.includes(key)) {
      return filter;
    }
    const convertedValue = convertQueryValueToFilterValue(query[key]);
    if (convertedValue !== undefined) {
      filter[key] = convertedValue;
    }
    return filter;
  }, {});
}

function parsePage(value: RawQueryValue) {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : undefined;
}

function parseAmount(value: RawQueryValue) {
  const amount = Number(value);
  return QUANTITY_OPTIONS.some((option) => option.id === amount) ? amount : undefined;
}

/**
 * Reads the search state carried by the URL query, validating each value.
 * Absent or invalid values come back as `undefined` so callers keep their own defaults.
 */
export function parseSearchQuery(query: LocationQuery): ParsedSearchQuery {
  const rawOrder = query.order as string | undefined;
  const rawSearchType = query.searchType as string | undefined;

  return {
    term: query.term as string | undefined,
    sort: query.sort as string | undefined,
    order: rawOrder === undefined ? undefined : toSortOrder(rawOrder),
    searchType: rawSearchType === 'name' || rawSearchType === 'prefix' ? rawSearchType : undefined,
    searchField: isMemberSearchField(query.searchField) ? query.searchField : undefined,
    page: query.page === undefined ? undefined : parsePage(query.page),
    amount: query.amount === undefined ? undefined : parseAmount(query.amount),
    filter: extractFilterFromQuery(query),
  };
}
